import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { api } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useLiveStats } from "../context/LiveStats";
import { useT } from "../lib/i18n";
import { Cover } from "./MixCard";
import { LiveNumber } from "./LiveNumber";
import { SectionTitle } from "./Reveal";

const Row = ({ mix, rank, list }) => {
  const { playMix, mix: current, playing } = usePlayer();
  const { t } = useT();
  const active = current?.id === mix.id && playing;
  return (
    <motion.li data-testid={`top-week-item-${rank}`} initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.6, delay: rank * 0.06 }}
      className="group grid grid-cols-[auto_auto_1fr_auto] items-center gap-4 sm:gap-6 py-4 border-t border-white/10 hover:bg-white/[0.03] transition-colors">
      <span className={`font-heavy text-4xl sm:text-6xl w-12 sm:w-20 ${rank === 1 ? "text-[#1FD1FF]" : "text-white/70"}`}>{String(rank).padStart(2, "0")}</span>
      <div className="w-14 h-14 sm:w-20 sm:h-20 overflow-hidden bg-[#0A1424]"><Cover mix={mix} /></div>
      <div className="min-w-0">
        <Link to={`/mix/${mix.id}`} data-testid={`top-week-title-${rank}`} className="font-display text-2xl sm:text-4xl leading-none hover:text-[#1FD1FF] transition-colors block truncate">{mix.title}</Link>
        <p className="font-mono text-xs text-[#A8B3C4] mt-1.5">
          <span data-testid={`top-week-plays-${rank}`}><LiveNumber value={mix.week_plays} className="text-white" /></span> {t("top.plays")}
        </p>
      </div>
      <button data-testid={`top-week-play-${rank}`} onClick={() => playMix(mix, list)} aria-label={active ? "Pause" : "Play"}
        className="w-12 h-12 rounded-full border border-[#1FD1FF]/60 text-[#1FD1FF] grid place-items-center hover:bg-[#1FD1FF] hover:text-black transition-colors">
        {active ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
      </button>
    </motion.li>
  );
};

export const TopWeek = () => {
  const { withOverrides } = usePlayer();
  const { counts } = useLiveStats();
  const { t } = useT();
  const [top, setTop] = useState([]);
  const plays = counts.totals?.plays;

  useEffect(() => {
    api.get("/mixes/top-week").then((r) => setTop(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [plays]);

  const list = top.map((m) => ({ ...withOverrides(m), week_plays: m.week_plays }));

  return (
    <section id="top" data-testid="top-week-section" className="max-w-[1500px] mx-auto px-4 lg:px-10 pt-24 lg:pt-36">
      <SectionTitle index="TOP 5" kicker={t("top.kicker")} title={<>{t("top.title")}<span className="text-[#1FD1FF]">.</span></>}>
        <p className="max-w-sm text-[#A8B3C4]">{t("top.body")}</p>
      </SectionTitle>
      {list.length === 0 ? (
        <p data-testid="top-week-empty" className="font-mono text-sm text-[#A8B3C4] py-12 text-center border border-dashed border-white/10">{t("top.empty")}</p>
      ) : (
        <ol className="border-b border-white/10">{list.map((m, i) => <Row key={m.id} mix={m} rank={i + 1} list={list} />)}</ol>
      )}
    </section>
  );
};
